import { generateKeyBetween } from "fractional-indexing";

import type { Schemas } from "@/shared/api/client";

export type Task = Schemas["TaskOut"];
export type TaskStatus = Schemas["TaskStatus"];
/** Columns shown on the board, in workflow order. Cancelled tasks stay off the board. */
export const BOARD_COLUMNS = [
  "backlog",
  "todo",
  "in_progress",
  "in_review",
  "done",
] as const satisfies readonly TaskStatus[];

export type BoardStatus = (typeof BOARD_COLUMNS)[number];
export type Board = Record<BoardStatus, Task[]>;

/** Matches the server's limit on task titles. */
export const TASK_TITLE_MAX_LENGTH = 300;

const COLUMN_ID_PREFIX = "column:";

export const columnDropId = (status: TaskStatus) => `${COLUMN_ID_PREFIX}${status}`;

export const TASK_STATUSES = [
  ...BOARD_COLUMNS,
  "cancelled",
] as const satisfies readonly TaskStatus[];

export function isTaskStatus(value: string): value is TaskStatus {
  return (TASK_STATUSES as readonly string[]).includes(value);
}

export function isBoardStatus(value: string): value is BoardStatus {
  return (BOARD_COLUMNS as readonly string[]).includes(value);
}

/** The status a column drop target stands for; null for anything else, such as a task id. */
export function columnStatusOf(dropId: string): BoardStatus | null {
  if (!dropId.startsWith(COLUMN_ID_PREFIX)) return null;
  const status = dropId.slice(COLUMN_ID_PREFIX.length);
  return isBoardStatus(status) ? status : null;
}

/** Byte-wise comparison, matching COLLATE "C" on the server. */
function compareKeys(left: string, right: string): number {
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

export function groupBoard(tasks: readonly Task[]): Board {
  const board = Object.fromEntries(BOARD_COLUMNS.map((status) => [status, [] as Task[]])) as Board;
  for (const task of tasks) {
    if (isBoardStatus(task.status)) board[task.status].push(task);
  }
  for (const status of BOARD_COLUMNS) {
    board[status].sort((a, b) => compareKeys(a.position, b.position));
  }
  return board;
}

export interface MovePlan {
  taskId: string;
  status: TaskStatus;
  afterId: string | null;
  beforeId: string | null;
  position: string;
}

function locate(board: Board, id: string): { status: BoardStatus; index: number } | null {
  for (const status of BOARD_COLUMNS) {
    const index = board[status].findIndex((task) => task.id === id);
    if (index !== -1) return { status, index };
  }
  return null;
}

/**
 * Where a dragged task lands when dropped on `overId` (a task or a column), with the new
 * fractional key computed exactly as the server will. Returns null when nothing changes.
 */
export function planMove(board: Board, taskId: string, overId: string): MovePlan | null {
  const from = locate(board, taskId);
  if (from === null || overId === taskId) return null;

  let status: BoardStatus;
  let targetIndex: number;
  const columnStatus = columnStatusOf(overId);
  if (columnStatus) {
    status = columnStatus;
    targetIndex = board[status].filter((task) => task.id !== taskId).length;
  } else {
    const over = locate(board, overId);
    if (over === null) return null;
    status = over.status;
    // Index into the column *without* the dragged task: dragging down within a column lands
    // after the hovered task, anything else lands before it (dnd-kit's arrayMove behaviour).
    targetIndex = over.index;
  }

  const remaining = board[status].filter((task) => task.id !== taskId);
  const clampedIndex = Math.min(targetIndex, remaining.length);
  if (from.status === status && from.index === clampedIndex) return null;

  const after = remaining[clampedIndex - 1] ?? null;
  const before = remaining[clampedIndex] ?? null;
  return {
    taskId,
    status,
    afterId: after?.id ?? null,
    beforeId: before?.id ?? null,
    position: generateKeyBetween(after?.position ?? null, before?.position ?? null),
  };
}

/**
 * A drop on a board of many projects: only the status changes. Card order belongs to each
 * project, so the server places the card at the end of its own project's column.
 */
export function planStatusMove(board: Board, taskId: string, overId: string): MovePlan | null {
  const from = locate(board, taskId);
  if (from === null || overId === taskId) return null;
  const status = columnStatusOf(overId) ?? locate(board, overId)?.status ?? null;
  if (status === null || status === from.status) return null;
  const task = board[from.status][from.index];
  if (task === undefined) return null;
  return { taskId, status, afterId: null, beforeId: null, position: task.position };
}

/** Columns of tasks in the order given (the server's sort), not by board position. */
export function groupByStatus(tasks: readonly Task[]): Board {
  const board = Object.fromEntries(BOARD_COLUMNS.map((status) => [status, [] as Task[]])) as Board;
  for (const task of tasks) {
    if (isBoardStatus(task.status)) board[task.status].push(task);
  }
  return board;
}

/** The Done column shows recent work only; older done tasks stay one click away in the list. */
export const DONE_RECENT_DAYS = 14;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Done tasks completed in the last DONE_RECENT_DAYS days, in board order. */
export function recentlyDone(done: readonly Task[], now: number = Date.now()): Task[] {
  const since = now - DONE_RECENT_DAYS * MS_PER_DAY;
  return done.filter(
    (task) => task.completed_at === null || Date.parse(task.completed_at) >= since,
  );
}
